import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash-es';
import classNames from 'classnames';
import dateFormat from 'dateformat';
/* Models */
import { InventoryIncentivesEntities } from 'client/data/models/inventory-incentives';
import { Incentive } from 'site-modules/shared/constants/car-buying/models';
/* Utils */
import { formatPriceString } from 'site-modules/shared/utils/price-utils';
import { isLeaseIncentivePrimary, isIncentivePrimary } from 'site-modules/shared/utils/incentives/incentives-service';
/* Constants */
/* Components */
import { Checkbox } from 'site-modules/shared/components/checkbox/checkbox';
import { PriceCheckerIncentiveInfoModal } from 'site-modules/shared/components/price-checker/prise-checker-incentive-info-modal/prise-checker-incentive-info-modal';

import './price-checker-incentives-list-item.scss';

export function PriceCheckerIncentivesListItem({
  incentive = {},
  onClick = noop,
  isLeaseView = false,
  incompatibleIncentivesIds = {},
  selectedIncentives = [],
  isUnselectable = false,
  isNonTMVDealer = false,
  isInitiallyFocused = false,
}) {
  const { name, endDate, rebateAmount, id, restrictions, startDate, subtype } = incentive;

  const ref = useRef();

  const incentiveTitle = name || subtype;

  useEffect(() => {
    if (isInitiallyFocused) ref.current?.focus();
  }, [isInitiallyFocused]);

  const priceString = rebateAmount > 0 ? formatPriceString(rebateAmount) : null;

  function handleChange() {
    onClick(incentive);
  }

  const isIncentiveChecked = !!selectedIncentives.find(selectedIncentive => selectedIncentive.id === id);
  const isDisabled = !isIncentiveChecked && !!incompatibleIncentivesIds[id];

  const priceElement = (
    <div className="text-nowrap text-end">
      {!!rebateAmount && (
        <div
          className={classNames('fw-bold', {
            'text-cool-gray-30': !isDisabled,
            'text-cool-gray-60': isDisabled,
          })}
        >
          -{priceString}
        </div>
      )}
      <div>
        {endDate ? (
          <i className="small text-cool-gray-40">
            &nbsp;
            {`Expires ${dateFormat(new Date(endDate), 'm/d/yy')}`}
          </i>
        ) : null}
      </div>
    </div>
  );

  const isPrimary = isLeaseView ? isLeaseIncentivePrimary(incentive) : isIncentivePrimary(incentive);

  const isCashValueValid = isPrimary || rebateAmount > 0;

  const availableCopy =
    isPrimary && !isNonTMVDealer ? (
      <span
        className={classNames('medium fw-medium fst-italic', {
          'text-green-30': !isDisabled,
          'text-green-80': isDisabled,
        })}
      >
        available to all
      </span>
    ) : (
      ''
    );

  return isCashValueValid && !isUnselectable ? (
    <li
      className={classNames('incentives-list-item is-selectable d-flex justify-content-between align-items-top pos-r', {
        'text-cool-gray-30': !isDisabled,
        'text-cool-gray-60': isDisabled,
        'pb-1_25': !rebateAmount,
      })}
      key={id}
    >
      <div>
        <Checkbox
          ref={ref}
          id={`incentiveId-${id}`}
          readOnly
          checked={isIncentiveChecked}
          disabled={isDisabled}
          onChange={!isDisabled ? handleChange : null}
          containerClassName="incentives-list-item-checkbox d-inline top-0 left-0"
          customLabelClassName={classNames({ disabled: isDisabled })}
          textLabelClassName="incentives-list-item-checkbox-label ps-1_75 ps-md-2 pe-0 d-inline"
          labelText={incentiveTitle}
          labelClassName="d-inline"
        />
        {restrictions ? (
          <PriceCheckerIncentiveInfoModal
            isDisabled={isDisabled}
            rebateAmount={rebateAmount}
            endDate={endDate}
            restrictions={restrictions}
            startDate={startDate}
            incentiveTitle={incentiveTitle}
            infoClasses={classNames('me-0_5', {
              'text-cool-gray-30': !isDisabled,
              'text-cool-gray-60': isDisabled,
            })}
          />
        ) : null}
        {availableCopy}
      </div>
      {priceElement}
    </li>
  ) : (
    <li
      className={classNames(
        'incentives-list-item d-flex justify-content-between align-items-top pos-r mb-0_5, text-cool-gray-30',
        {
          'pb-1_25': !rebateAmount,
        }
      )}
      key={id}
    >
      <div className="pe-0_5">
        {incentiveTitle}

        {!!restrictions && (
          <PriceCheckerIncentiveInfoModal
            rebateAmount={rebateAmount}
            endDate={endDate}
            restrictions={restrictions}
            startDate={startDate}
            incentiveTitle={incentiveTitle}
            infoClasses="text-cool-gray-30 me-0_5"
          />
        )}
        {availableCopy}
      </div>
      {priceElement}
    </li>
  );
}

PriceCheckerIncentivesListItem.propTypes = {
  incentive: PropTypes.oneOfType([Incentive, InventoryIncentivesEntities.Incentive]),
  onClick: PropTypes.func,
  isLeaseView: PropTypes.bool,
  incompatibleIncentivesIds: PropTypes.shape({}),
  selectedIncentives: PropTypes.arrayOf(PropTypes.oneOfType([Incentive, InventoryIncentivesEntities.Incentive])),
  isUnselectable: PropTypes.bool,
  isNonTMVDealer: PropTypes.bool,
  isInitiallyFocused: PropTypes.bool,
};
